import type { TrackControlPoint, TrackDefinition } from '../core/types';

/** Bank values are radians. Coordinates and widths are metres. */
const point = (
  x: number,
  y: number,
  z: number,
  options: Omit<TrackControlPoint, 'x' | 'y' | 'z'> = {},
): TrackControlPoint => ({ x, y, z, ...options });

export const TRACKS: readonly TrackDefinition[] = [
  {
    id: 'first-run',
    name: 'First Run',
    subtitle: 'Coastal proving ground',
    difficulty: 'Easy',
    theme: 'coast',
    description:
      'A broad, readable introduction that builds from gentle sweepers to a forgiving jump and a full-throttle run home.',
    memorableFeature: 'A sunlit training ramp followed by the ocean-view booster straight.',
    targetTime: [45, 75],
    roadWidth: 16,
    points: [
      point(0, 2, 0, { width: 17, barriers: 'both' }),
      point(0, 2, 95, { width: 17, barriers: 'both' }),
      point(8, 2, 190, { width: 17, barriers: 'both' }),
      point(42, 3, 278, { width: 17, barriers: 'both' }),
      point(103, 4, 344, { width: 16, barriers: 'both' }),
      point(181, 4, 382, { width: 16, barriers: 'both' }),
      point(267, 4, 380, { width: 16, barriers: 'both' }),
      point(344, 5, 344, { width: 16, barriers: 'both' }),
      point(397, 6, 276, { width: 16, barriers: 'both' }),
      point(425, 9, 193, { width: 16, barriers: 'both' }),
      point(426, 13, 109, { width: 16, barriers: 'both' }),
      point(404, 20, 29, { width: 16, barriers: 'both', jump: true }),
      point(356, 8, -61, { width: 17, barriers: 'both' }),
      point(286, 3, -132, { width: 17, barriers: 'both' }),
      point(201, 2, -171, { width: 17, barriers: 'both' }),
      point(109, 2, -172, { width: 17, barriers: 'both' }),
      point(27, 2, -136, { width: 17, barriers: 'both' }),
      point(-26, 2, -67, { width: 17, barriers: 'both' }),
      point(-38, 2, 22, { width: 17, barriers: 'both' }),
      point(-24, 2, 116, { width: 17, barriers: 'both' }),
      point(3, 2, 211, { width: 18, barriers: 'both' }),
      point(35, 2, 310, { width: 18, barriers: 'both' }),
    ],
    checkpointFractions: [0.14, 0.28, 0.42, 0.57, 0.72, 0.87],
    boosterFractions: [0.77],
    jumpFractions: [0.535],
    bankedFractions: [[0.31, 0.4, -0.1]],
    palette: {
      sky: 0x8ed9ff,
      fog: 0xbfeaff,
      ground: 0x477a52,
      road: 0x3b4650,
      edge: 0xf1f5ed,
      accent: 0x16cdf2,
      secondary: 0xffc642,
    },
  },
  {
    id: 'pine-ridge',
    name: 'Pine Ridge',
    subtitle: 'Climb above the tree line',
    difficulty: 'Easy / Medium',
    theme: 'forest',
    description:
      'Long mountain sweepers climb through pines, skim a cliff face and leap a wooded ravine before descending to the valley.',
    memorableFeature: 'A timber-framed ravine jump with the mountain panorama beyond it.',
    targetTime: [60, 100],
    roadWidth: 14,
    points: [
      point(0, 4, 0, { width: 15, barriers: 'both' }),
      point(18, 7, 102, { width: 15, barriers: 'both' }),
      point(67, 15, 202, { width: 15, barriers: 'both' }),
      point(143, 28, 280, { width: 14, barriers: 'both' }),
      point(239, 44, 324, { width: 14, barriers: 'right' }),
      point(342, 60, 326, { width: 14, barriers: 'right' }),
      point(438, 75, 285, { width: 13.5, barriers: 'right' }),
      point(505, 87, 210, { width: 13.5, barriers: 'right' }),
      point(535, 97, 117, { width: 13, barriers: 'right' }),
      point(523, 105, 24, { width: 13, barriers: 'right' }),
      point(476, 112, -62, { width: 13, barriers: 'right' }),
      point(399, 119, -126, { width: 13, barriers: 'right' }),
      point(304, 123, -157, { width: 13.5, barriers: 'both' }),
      point(207, 119, -143, { width: 14, barriers: 'both' }),
      point(133, 109, -86, { width: 14, barriers: 'left' }),
      point(96, 96, -1, { width: 14, barriers: 'left' }),
      point(105, 83, 94, { width: 14, barriers: 'left' }),
      point(157, 73, 172, { width: 13, barriers: 'both' }),
      point(235, 70, 222, { width: 12.5, barriers: 'both', jump: true }),
      point(337, 58, 245, { width: 13, barriers: 'both' }),
      point(440, 50, 231, { width: 13.5, barriers: 'both' }),
      point(538, 42, 185, { width: 14, barriers: 'left' }),
      point(623, 34, 112, { width: 14, barriers: 'left' }),
      point(706, 26, 29, { width: 14, barriers: 'both' }),
      point(801, 19, -30, { width: 15, barriers: 'both' }),
      point(908, 13, -50, { width: 15, barriers: 'both' }),
      point(1015, 9, -25, { width: 16, barriers: 'both' }),
    ],
    checkpointFractions: [0.11, 0.22, 0.34, 0.46, 0.58, 0.7, 0.82, 0.92],
    boosterFractions: [0.075, 0.715],
    jumpFractions: [0.665],
    openFractions: [[0.64, 0.69]],
    bankedFractions: [
      [0.19, 0.31, -0.16],
      [0.76, 0.84, 0.12],
    ],
    palette: {
      sky: 0x88adc4,
      fog: 0x9db3ad,
      ground: 0x173f2d,
      road: 0x3d4240,
      edge: 0xd5caa7,
      accent: 0xf4a83c,
      secondary: 0x8bd17c,
    },
  },
  {
    id: 'neon-circuit',
    name: 'Neon Circuit',
    subtitle: 'Midnight precision',
    difficulty: 'Medium',
    theme: 'neon',
    description:
      'A glowing city course of abrupt direction changes, a light-tube tunnel, a steeply banked bowl and a blistering final avenue.',
    memorableFeature: 'The cyan-magenta velocity bowl wrapped between skyscrapers.',
    targetTime: [60, 100],
    roadWidth: 12,
    points: [
      point(0, 6, 0, { width: 13, barriers: 'both' }),
      point(0, 6, 100, { width: 13, barriers: 'both' }),
      point(2, 6, 205, { width: 13, barriers: 'both' }),
      point(28, 7, 297, { width: 12, barriers: 'both' }),
      point(95, 8, 349, { width: 11, barriers: 'both' }),
      point(178, 8, 332, { width: 11, barriers: 'both' }),
      point(213, 8, 263, { width: 10.5, barriers: 'both' }),
      point(174, 8, 200, { width: 10.5, barriers: 'both' }),
      point(92, 8, 186, { width: 11, barriers: 'both' }),
      point(29, 8, 139, { width: 11, barriers: 'both' }),
      point(31, 8, 62, { width: 11, barriers: 'both' }),
      point(97, 8, 20, { width: 11, barriers: 'both' }),
      point(177, 9, 47, { width: 11.5, barriers: 'both' }),
      point(234, 10, 117, { width: 11.5, barriers: 'both' }),
      point(305, 11, 165, { width: 11, barriers: 'both' }),
      point(386, 12, 143, { width: 11, barriers: 'both' }),
      point(422, 14, 71, { width: 11, barriers: 'both' }),
      point(400, 17, -13, { width: 11, barriers: 'both' }),
      point(337, 22, -80, { width: 12, barriers: 'both' }),
      point(268, 30, -143, { width: 12, barriers: 'both' }),
      point(244, 38, -231, { width: 12, barriers: 'both' }),
      point(281, 43, -310, { width: 12.5, barriers: 'both' }),
      point(365, 45, -343, { width: 13, barriers: 'both' }),
      point(462, 43, -320, { width: 13, barriers: 'both' }),
      point(555, 37, -267, { width: 13.5, barriers: 'both' }),
      point(647, 28, -199, { width: 14, barriers: 'both' }),
      point(741, 18, -120, { width: 14, barriers: 'both' }),
      point(841, 10, -32, { width: 15, barriers: 'both' }),
      point(952, 7, 57, { width: 16, barriers: 'both' }),
    ],
    checkpointFractions: [0.09, 0.19, 0.3, 0.41, 0.52, 0.63, 0.74, 0.85, 0.94],
    boosterFractions: [0.105, 0.49, 0.825, 0.9],
    tunnelFractions: [[0.255, 0.37]],
    bankedFractions: [
      [0.55, 0.69, 0.34],
      [0.7, 0.78, -0.18],
    ],
    palette: {
      sky: 0x040616,
      fog: 0x11152f,
      ground: 0x080a13,
      road: 0x1c2032,
      edge: 0x7dfcff,
      accent: 0xff28c8,
      secondary: 0x57ff6a,
    },
  },
  {
    id: 'dust-devil',
    name: 'Dust Devil',
    subtitle: 'Canyon air, desert speed',
    difficulty: 'Medium',
    theme: 'desert',
    description:
      'Huge sandstone sweepers crest a mesa, plunge through a dry riverbed and launch across two sun-baked canyon cuts.',
    memorableFeature: 'A booster-fed ramp that clears the red-rock chasm in one committed leap.',
    targetTime: [75, 120],
    roadWidth: 15,
    points: [
      point(0, 8, 0, { width: 16, barriers: 'both' }),
      point(18, 9, 115, { width: 16, barriers: 'both' }),
      point(72, 13, 220, { width: 16, barriers: 'both' }),
      point(156, 19, 306, { width: 16, barriers: 'left' }),
      point(263, 28, 357, { width: 16, barriers: 'left' }),
      point(380, 39, 365, { width: 15, barriers: 'left' }),
      point(493, 49, 329, { width: 15, barriers: 'left' }),
      point(582, 56, 252, { width: 15, barriers: 'both' }),
      point(634, 60, 150, { width: 15, barriers: 'both' }),
      point(644, 56, 36, { width: 15, barriers: 'right' }),
      point(609, 47, -78, { width: 16, barriers: 'right' }),
      point(534, 34, -171, { width: 17, barriers: 'right' }),
      point(432, 20, -237, { width: 18, barriers: 'both' }),
      point(314, 7, -267, { width: 18, barriers: 'both' }),
      point(192, -3, -251, { width: 18, barriers: 'none' }),
      point(82, -8, -190, { width: 18, barriers: 'none' }),
      point(5, -6, -98, { width: 17, barriers: 'both', jump: true }),
      point(-44, -17, 9, { width: 17, barriers: 'both' }),
      point(-56, -20, 125, { width: 17, barriers: 'right' }),
      point(-25, -15, 240, { width: 16, barriers: 'right' }),
      point(45, -5, 339, { width: 16, barriers: 'right' }),
      point(146, 8, 410, { width: 16, barriers: 'both' }),
      point(263, 23, 447, { width: 15, barriers: 'both' }),
      point(383, 37, 442, { width: 15, barriers: 'both', jump: true }),
      point(505, 25, 405, { width: 16, barriers: 'both' }),
      point(624, 14, 350, { width: 17, barriers: 'none' }),
      point(739, 4, 289, { width: 18, barriers: 'both', jump: true }),
      point(866, -9, 246, { width: 18, barriers: 'both' }),
      point(996, -4, 244, { width: 18, barriers: 'both' }),
      point(1125, 3, 285, { width: 18, barriers: 'both' }),
      point(1250, 8, 349, { width: 18, barriers: 'both' }),
    ],
    checkpointFractions: [0.09, 0.18, 0.27, 0.36, 0.46, 0.56, 0.66, 0.76, 0.85, 0.93],
    boosterFractions: [0.45, 0.725, 0.84],
    jumpFractions: [0.485, 0.745, 0.86],
    openFractions: [
      [0.455, 0.505],
      [0.725, 0.77],
      [0.84, 0.88],
    ],
    bankedFractions: [[0.16, 0.29, -0.18]],
    palette: {
      sky: 0xf2a55f,
      fog: 0xd78a4e,
      ground: 0xa84e26,
      road: 0x57443c,
      edge: 0xffd28a,
      accent: 0xffd43b,
      secondary: 0x68d5c8,
    },
  },
  {
    id: 'skyway',
    name: 'Skyway',
    subtitle: 'Nothing beneath the apex',
    difficulty: 'Hard',
    theme: 'sky',
    description:
      'A narrow suspended ribbon links floating platforms with booster chains, exposed banking and long jumps above the clouds.',
    memorableFeature: 'The barrier-free triple boost across the cloud sea.',
    targetTime: [75, 120],
    roadWidth: 11,
    points: [
      point(0, 185, 0, { width: 12, barriers: 'both' }),
      point(0, 185, 105, { width: 12, barriers: 'both' }),
      point(13, 188, 211, { width: 11, barriers: 'both' }),
      point(57, 196, 307, { width: 11, barriers: 'right' }),
      point(132, 209, 381, { width: 10.5, barriers: 'right' }),
      point(230, 224, 418, { width: 10.5, barriers: 'none' }),
      point(339, 237, 412, { width: 10, barriers: 'none', jump: true }),
      point(454, 226, 373, { width: 10.5, barriers: 'both' }),
      point(552, 211, 303, { width: 10.5, barriers: 'both' }),
      point(613, 198, 211, { width: 10, barriers: 'left' }),
      point(631, 191, 104, { width: 10, barriers: 'left' }),
      point(603, 192, 0, { width: 10, barriers: 'left' }),
      point(537, 202, -82, { width: 10, barriers: 'none' }),
      point(443, 219, -126, { width: 9.5, barriers: 'none' }),
      point(335, 239, -128, { width: 9.5, barriers: 'none', jump: true }),
      point(232, 224, -88, { width: 10, barriers: 'both' }),
      point(154, 208, -14, { width: 10, barriers: 'both' }),
      point(111, 198, 84, { width: 9.5, barriers: 'right' }),
      point(113, 198, 190, { width: 9.5, barriers: 'right' }),
      point(161, 210, 284, { width: 9.5, barriers: 'none' }),
      point(246, 231, 349, { width: 9, barriers: 'none' }),
      point(356, 253, 373, { width: 9, barriers: 'none', jump: true }),
      point(468, 240, 350, { width: 10, barriers: 'both' }),
      point(568, 222, 293, { width: 10, barriers: 'both' }),
      point(655, 210, 217, { width: 10, barriers: 'none' }),
      point(750, 215, 157, { width: 10, barriers: 'none' }),
      point(862, 229, 139, { width: 10, barriers: 'none', jump: true }),
      point(978, 216, 163, { width: 10.5, barriers: 'both' }),
      point(1085, 201, 220, { width: 11, barriers: 'both' }),
      point(1184, 191, 301, { width: 12, barriers: 'both' }),
      point(1283, 187, 390, { width: 13, barriers: 'both' }),
    ],
    checkpointFractions: [0.085, 0.18, 0.28, 0.38, 0.48, 0.58, 0.68, 0.78, 0.87, 0.94],
    boosterFractions: [0.185, 0.205, 0.225, 0.61, 0.635, 0.66, 0.835],
    jumpFractions: [0.215, 0.47, 0.685, 0.85],
    openFractions: [
      [0.17, 0.245],
      [0.395, 0.48],
      [0.595, 0.7],
      [0.795, 0.87],
    ],
    bankedFractions: [
      [0.3, 0.4, -0.32],
      [0.7, 0.79, 0.29],
    ],
    palette: {
      sky: 0x77c8ff,
      fog: 0xd7efff,
      ground: 0xd9f1ff,
      road: 0x394458,
      edge: 0xf4fbff,
      accent: 0x36efff,
      secondary: 0xff6dc8,
    },
  },
  {
    id: 'the-gauntlet',
    name: 'The Gauntlet',
    subtitle: 'Everything, all at once',
    difficulty: 'Hard',
    theme: 'gauntlet',
    description:
      'The final exam combines climbing switchbacks, a booster chasm, a long airborne transfer, high-speed banking and an unforgiving last sector.',
    memorableFeature: 'The Forge Leap into a wall-riding bank above the finish complex.',
    targetTime: [90, 150],
    roadWidth: 12,
    points: [
      point(0, 12, 0, { width: 14, barriers: 'both' }),
      point(0, 12, 105, { width: 14, barriers: 'both' }),
      point(20, 15, 211, { width: 13, barriers: 'both' }),
      point(78, 24, 299, { width: 12, barriers: 'both' }),
      point(169, 38, 349, { width: 11.5, barriers: 'right' }),
      point(271, 54, 342, { width: 11.5, barriers: 'right' }),
      point(344, 68, 276, { width: 11, barriers: 'right' }),
      point(356, 79, 182, { width: 11, barriers: 'both' }),
      point(302, 86, 105, { width: 10.5, barriers: 'both' }),
      point(212, 88, 86, { width: 10.5, barriers: 'both' }),
      point(145, 90, 137, { width: 10.5, barriers: 'left' }),
      point(142, 96, 225, { width: 10.5, barriers: 'left' }),
      point(205, 106, 291, { width: 11, barriers: 'both' }),
      point(300, 117, 312, { width: 11, barriers: 'both' }),
      point(397, 126, 284, { width: 11.5, barriers: 'both' }),
      point(474, 130, 217, { width: 12, barriers: 'both', jump: true }),
      point(552, 111, 132, { width: 12, barriers: 'none' }),
      point(648, 91, 76, { width: 12, barriers: 'both' }),
      point(752, 79, 63, { width: 12, barriers: 'both' }),
      point(856, 74, 93, { width: 12.5, barriers: 'both' }),
      point(942, 78, 159, { width: 12.5, barriers: 'both' }),
      point(992, 90, 249, { width: 12, barriers: 'left' }),
      point(993, 106, 353, { width: 11.5, barriers: 'left' }),
      point(946, 121, 448, { width: 11, barriers: 'none' }),
      point(865, 136, 521, { width: 11, barriers: 'none', jump: true }),
      point(766, 111, 573, { width: 11.5, barriers: 'none' }),
      point(657, 82, 596, { width: 12, barriers: 'both' }),
      point(548, 61, 582, { width: 12, barriers: 'both' }),
      point(453, 51, 531, { width: 11.5, barriers: 'right' }),
      point(388, 53, 448, { width: 11, barriers: 'right' }),
      point(370, 67, 353, { width: 11, barriers: 'right' }),
      point(407, 86, 267, { width: 11, barriers: 'right' }),
      point(482, 101, 203, { width: 11, barriers: 'both' }),
      point(579, 109, 176, { width: 11.5, barriers: 'both' }),
      point(679, 106, 196, { width: 12, barriers: 'both' }),
      point(765, 94, 254, { width: 12, barriers: 'both' }),
      point(825, 75, 338, { width: 11.5, barriers: 'both' }),
      point(840, 54, 435, { width: 11, barriers: 'both' }),
      point(806, 36, 530, { width: 10.5, barriers: 'both' }),
      point(731, 23, 606, { width: 10.5, barriers: 'both' }),
      point(628, 16, 642, { width: 11, barriers: 'both' }),
      point(516, 13, 629, { width: 12, barriers: 'both' }),
      point(408, 12, 583, { width: 13, barriers: 'both' }),
      point(307, 12, 516, { width: 13.5, barriers: 'both' }),
      point(205, 12, 450, { width: 14, barriers: 'both' }),
      point(92, 12, 406, { width: 14, barriers: 'both' }),
      point(-26, 12, 393, { width: 15, barriers: 'both' }),
    ],
    checkpointFractions: [0.07, 0.14, 0.22, 0.3, 0.38, 0.46, 0.54, 0.62, 0.7, 0.78, 0.86, 0.93],
    boosterFractions: [0.345, 0.535, 0.55, 0.565, 0.805],
    jumpFractions: [0.35, 0.555],
    openFractions: [
      [0.335, 0.375],
      [0.525, 0.585],
    ],
    tunnelFractions: [[0.665, 0.735]],
    bankedFractions: [
      [0.4, 0.49, 0.18],
      [0.59, 0.665, -0.4],
      [0.805, 0.875, 0.26],
    ],
    palette: {
      sky: 0x17111d,
      fog: 0x35273a,
      ground: 0x251d29,
      road: 0x36343d,
      edge: 0xffeee0,
      accent: 0xff4a25,
      secondary: 0x4fe5ff,
    },
  },
] as const;

export const TRACK_DEFINITIONS = TRACKS;
export const DEFAULT_TRACK_ID = 'first-run';

export const TRACK_BY_ID: Readonly<Record<string, TrackDefinition>> = Object.freeze(
  Object.fromEntries(TRACKS.map((track) => [track.id, track])),
);

export function getTrackDefinition(id: string): TrackDefinition {
  return TRACK_BY_ID[id] ?? TRACK_BY_ID[DEFAULT_TRACK_ID];
}

export const getTrackById = getTrackDefinition;
